import { createSlice, PayloadAction } from "@reduxjs/toolkit";
import {
  AcademicYearResponse,
  DegreeResponse,
  EnrollmentDateFilterDto,
  FacultyResponse,
  FeeTypeResponse,
  SpecialityModeResponse,
  SpecialityResponse,
  StudentStatusResponse,
} from "../../models/dtos";
import { RootState } from "../../store";
import { StudentsState } from "./studentsState";
import { FilterField } from "./filterField";

const filterFields: FilterField<any>[] = [
  { key: "faculties", urlParam: "faculties", idField: "facultyId", isArray: true },
  { key: "degrees", urlParam: "degrees", idField: "degreeId", isArray: true },
  { key: "specialities", urlParam: "specialities", idField: "specialityId", isArray: true },
  { key: "courseYears", urlParam: "courses", idField: "course", isArray: true },
  { key: "eduForms", urlParam: "eduForms", idField: "specialityModeId", isArray: true },
  { key: "fees", urlParam: "fees", idField: "feeTypeId", isArray: true },
  { key: "academicYears", urlParam: "academicYears", idField: "academicYearId", isArray: true },
  { key: "studentStatuses", urlParam: "statuses", idField: "studentStatusId", isArray: true },
];

export interface StudentFilters {
  faculties: FacultyResponse[];
  degrees: DegreeResponse[];
  specialities: SpecialityResponse[];
  courseYears: { course: number }[];
  eduForms: SpecialityModeResponse[];
  fees: FeeTypeResponse[];
  academicYears: AcademicYearResponse[];
  studentStatuses: StudentStatusResponse[];
  enrollmentDates: EnrollmentDateFilterDto;
  isForeigner: boolean;
  isStudyingInEnglish: boolean;
  isBulgarianCitizen: boolean;
}

const initialState: StudentsState = {
  filters: {
    faculties: [],
    degrees: [],
    specialities: [],
    courseYears: [],
    eduForms: [],
    fees: [],
    academicYears: [],
    studentStatuses: [],
    enrollmentDates: {
      startDate: null,
      endDate: null,
    },
    isForeigner: false,
    isStudyingInEnglish: false,
    isBulgarianCitizen: false,
  },
};

export const serializeFilters = (filters: StudentFilters) => {
  const params = new URLSearchParams();

  // Handle array filters
  filterFields.forEach((field) => {
    const values = filters[field.key];
    if (field.isArray && Array.isArray(values) && values.length > 0 && field.idField) {
      params.set(field.urlParam, (values as any[]).map((x) => x[field.idField!]).join(","));
    }
  });

  if (filters.enrollmentDates?.startDate || filters.enrollmentDates?.endDate) {
    if (filters.enrollmentDates.startDate) {
      const startDate =
        typeof filters.enrollmentDates.startDate === "string"
          ? new Date(filters.enrollmentDates.startDate)
          : filters.enrollmentDates.startDate;
      params.set("enrollmentStart", startDate.toISOString());
    }
    if (filters.enrollmentDates.endDate) {
      const endDate =
        typeof filters.enrollmentDates.endDate === "string"
          ? new Date(filters.enrollmentDates.endDate)
          : filters.enrollmentDates.endDate;
      params.set("enrollmentEnd", endDate.toISOString());
    }
  }

  // Handle boolean filters
  if (filters.isForeigner) {
    params.set("isForeigner", "true");
  }
  if (filters.isStudyingInEnglish) {
    params.set("isStudyingInEnglish", "true");
  }
  if (filters.isBulgarianCitizen) {
    params.set("isBulgarianCitizen", "true");
  }

  return params.toString();
};

export const deserializeFilters = (queryString: string): StudentFilters => {
  const params = new URLSearchParams(queryString);
  const filters = { ...initialState.filters };

  filterFields.forEach((field) => {
    const param = params.get(field.urlParam);
    if (param && field.idField) {
      const values = param.split(",").map((id) => ({
        [field.idField!]: parseInt(id),
      }));
      (filters[field.key] as any[]) = values;
    }
  });

  if (params.get("enrollmentStart") || params.get("enrollmentEnd")) {
    filters.enrollmentDates = {
      startDate: params.get("enrollmentStart") ? new Date(params.get("enrollmentStart")!) : null,
      endDate: params.get("enrollmentEnd") ? new Date(params.get("enrollmentEnd")!) : null,
    };
  }

  // Handle boolean filters
  filters.isForeigner = params.get("isForeigner") === "true";
  filters.isStudyingInEnglish = params.get("isStudyingInEnglish") === "true";
  filters.isBulgarianCitizen = params.get("isBulgarianCitizen") === "true";

  return filters;
};

const studentsSlice = createSlice({
  name: "students",
  initialState,
  reducers: {
    setFilter: (state, action: PayloadAction<{ key: keyof StudentFilters; value: any }>) => {
      const { key, value } = action.payload;
      state.filters[key] = value;
    },
    clearStudentFilters: (state) => {
      state.filters = { ...initialState.filters };
    },
    initializeFromUrl: {
      reducer: (state, action: PayloadAction<string>) => {
        state.filters = deserializeFilters(action.payload);
      },
      prepare: (queryString: string) => ({
        payload: queryString,
      }),
    },
  },
});

export const { setFilter, clearStudentFilters, initializeFromUrl } = studentsSlice.actions;

export const setSelectedFaculties = (faculties: FacultyResponse[]) => setFilter({ key: "faculties", value: faculties });

export const setSelectedSpecialities = (specialities: SpecialityResponse[]) =>
  setFilter({ key: "specialities", value: specialities });

export const setSelectedDegrees = (degrees: DegreeResponse[]) => setFilter({ key: "degrees", value: degrees });

export const setSelectedCourseYears = (courseYears: { course: number }[]) =>
  setFilter({ key: "courseYears", value: courseYears });

export const setSelectedEduForms = (eduForms: SpecialityModeResponse[]) =>
  setFilter({ key: "eduForms", value: eduForms });

export const setSelectedFees = (fees: FeeTypeResponse[]) => setFilter({ key: "fees", value: fees });

export const setSelectedAcademicYears = (academicYears: AcademicYearResponse[]) =>
  setFilter({ key: "academicYears", value: academicYears });

export const setSelectedStudentStatuses = (statuses: StudentStatusResponse[]) =>
  setFilter({ key: "studentStatuses", value: statuses });

export const setSelectedEnrollmentDates = (dates: [Date | null, Date | null]) =>
  setFilter({
    key: "enrollmentDates",
    value: {
      startDate: dates[0],
      endDate: dates[1],
    },
  });

export const getStudentsFilters = (state: RootState) => state.students.filters;

export const getSelectedFaculties = (state: RootState) => state.students.filters.faculties;
export const getSelectedSpecialities = (state: RootState) => state.students.filters.specialities;
export const getSelectedDegrees = (state: RootState) => state.students.filters.degrees;
export const getSelectedCourseYears = (state: RootState) => state.students.filters.courseYears;
export const getSelectedEduForms = (state: RootState) => state.students.filters.eduForms;
export const getSelectedFees = (state: RootState) => state.students.filters.fees;
export const getSelectedAcademicYears = (state: RootState) => state.students.filters.academicYears;
export const getSelectedStudentStatuses = (state: RootState) => state.students.filters.studentStatuses;
export const getSelectedEnrollmentDates = (state: RootState) => state.students.filters.enrollmentDates;
export const getIsForeigner = (state: RootState) => state.students.filters.isForeigner;
export const getIsStudyingInEnglish = (state: RootState) => state.students.filters.isStudyingInEnglish;
export const getIsBulgarianCitizen = (state: RootState) => state.students.filters.isBulgarianCitizen;

export const setIsForeigner = (value: boolean) => setFilter({ key: "isForeigner", value });

export const setIsStudyingInEnglish = (value: boolean) => setFilter({ key: "isStudyingInEnglish", value });

export const setIsBulgarianCitizen = (value: boolean) => setFilter({ key: "isBulgarianCitizen", value });

export const getStudentsFiltersCount = (state: RootState) => {
  const filters = state.students.filters;
  return (
    filterFields.reduce((count, field) => {
      const values = filters[field.key];
      return count + (field.isArray && Array.isArray(values) ? values.length : 0);
    }, 0) + (filters.enrollmentDates?.startDate || filters.enrollmentDates?.endDate ? 1 : 0)
  );
};

export default studentsSlice.reducer;
